import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { parseDisplayNumericReading } from '@immo/api-contract';
import type { DossierEntry, DossierNumberFormat } from '@immo/api-contract';

const NS = 'pages.admin.properties.detail.dossier';

/** What the Dossier prints for a value it has nothing at all to show. */
export const EMPTY = '—';

/**
 * Renders one Dossier fact value in the operator's language (IMM-686).
 *
 * ONE renderer for both call sites — „Bestätigter Wert" and „Erkannte Werte" —
 * because the bug was that the second was looked at and the first was not. The
 * two disagreeing again is the failure mode this replaces (BL-082); a single
 * function is what makes that impossible rather than merely unlikely.
 *
 * Switching on the taxonomy's declared `valueType`, never on the shape of the
 * runtime value: `typeof value === 'string'` cannot tell „DEMAND" (an enum whose
 * label exists) from a free-text street name, and the reading is stored as raw
 * JSON, so `'120'` and `120` are both live for the same numeric key.
 */
export function useDossierValueFormatter(): (
  entry: Pick<DossierEntry, 'factKey' | 'valueType' | 'numberFormat'>,
  value: unknown,
  factUnit?: string | null,
) => string {
  const intl = useIntl();

  return useCallback(
    (entry, value, factUnit) => {
      if (value === null || value === undefined) return EMPTY;

      switch (entry.valueType) {
        case 'enum':
          return enumLabel(intl, entry.factKey, value);
        case 'boolean':
          return booleanLabel(intl, value);
        case 'date':
          return dateLabel(intl, value);
        case 'number':
          return numberLabel(intl, entry.numberFormat, value, factUnit);
        default:
          return withUnit(plain(value), factUnit ?? null);
      }
    },
    [intl],
  );
}

type Intl = ReturnType<typeof useIntl>;

/**
 * The German label for an enum reading, or the raw literal when the shared map
 * has none. Degrading to the literal rather than throwing is deliberate: an
 * operator seeing „DEMAND" on one row has lost nothing they had before the fix,
 * whereas a screen that crashes on one unmapped value takes the whole Dossier
 * down. The gap is reported by `dossier-value-labels.test.ts` instead, which
 * fails in CI on exactly this case — visible to us, invisible to them.
 *
 * `intl.messages` is consulted rather than `formatMessage`, which logs an error
 * and returns the message id („pages.admin.….DEMAND") — a worse thing to print
 * than the literal, and noise in every test that renders an unmapped value.
 */
function enumLabel(intl: Intl, factKey: string, value: unknown): string {
  const literal = plain(value);
  const id = `${NS}.enum-values.${factKey}.${literal}`;
  const label = intl.messages[id];

  return typeof label === 'string' ? label : literal;
}

function booleanLabel(intl: Intl, value: unknown): string {
  // The reading is raw JSON, so a boolean key can arrive as `true` or as the
  // string `'true'` depending on the extractor. Both mean the same thing.
  const truthy = value === true || value === 'true';
  const falsy = value === false || value === 'false';

  if (!truthy && !falsy) return plain(value);

  return intl.formatMessage({ id: `${NS}.boolean-values.${truthy ? 'true' : 'false'}` });
}

/**
 * A `date` reading is a date-only string, and `new Date('2025-07-02')` is UTC
 * midnight — so any timezone west of UTC would render the PREVIOUS day. The
 * explicit midnight suffix makes it parse as local midnight instead, the same
 * treatment `desiredCompletionLabel` in `valuation-requests.tsx` already applies
 * for the same reason. A value that already carries a time is left alone.
 */
function dateLabel(intl: Intl, value: unknown): string {
  const literal = plain(value);
  const local = /^\d{4}-\d{2}-\d{2}$/.test(literal) ? `${literal}T00:00:00` : literal;
  const parsed = new Date(local);

  // An unparseable date renders as stored rather than as „Invalid Date".
  if (Number.isNaN(parsed.getTime())) return literal;

  return intl.formatDate(parsed, { dateStyle: 'medium' });
}

/**
 * A number in its declared format. The two styles differ in GROUPING only —
 * `PLAIN_INTEGER` suppresses the separator, because a grouped „1.968" for
 * `yearBuilt` reads as a quantity rather than a year (IMM-444).
 *
 * NEITHER rounds. The keys are declared with the precision their data has, so
 * rounding is a no-op on good data — and on bad data it silently hides the
 * reading an operator most needs to see: a `408,5` extracted for a key that
 * cannot have a fraction, or the 75 cents of an `850,75 €` Kaltmiete. This is a
 * provenance-comparison screen; a value it quietly reshapes is a value it lies
 * about.
 *
 * A key with no declaration at all falls back to a plain integer, matching the
 * taxonomy's documented default, and only THEN takes the fact's own extracted
 * unit — a key whose declaration says `unit: null` said so deliberately (the
 * „3,5 Zimmer Zimmer" case migration 0030 exists to prevent), so its null must
 * not be filled in from the reading.
 */
function numberLabel(intl: Intl, format: DossierNumberFormat | null, value: unknown, factUnit?: string | null): string {
  const literal = plain(value);
  // Shares the backend's SEPARATOR rule rather than re-deriving it (BL-022): a
  // bare `Number()` read a German-grouped „450.000" as 450, so the Dossier would
  // have shown „450 €" for a document that says 450.000 €. Strict about what
  // counts as numeric at all, unlike the coercion path — see the docblock there.
  const numeric = parseDisplayNumericReading(value);
  const unit = format === null ? (factUnit ?? null) : format.unit;

  // An unreadable reading is shown as stored, never dressed up: a bare „ m²"
  // reads as a unit whose value went missing rather than as a value that was
  // never a number, and an empty one has nothing to show at all.
  if (numeric === null) return literal.trim() === '' ? EMPTY : literal;

  const rendered = intl.formatNumber(numeric, {
    useGrouping: format?.style === 'GROUPED_DECIMAL',
    maximumFractionDigits: 20,
  });

  return withUnit(rendered, unit);
}

function plain(value: unknown): string {
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

function withUnit(rendered: string, unit: string | null): string {
  return unit === null || unit === '' ? rendered : `${rendered} ${unit}`;
}
